"use client";

import React, { useRef } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { SearchPagination } from "@ama-pt/agora-design-system";
import { useTranslations } from "next-intl";

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
}

export default function PaginationControls({
  totalPages,
}: PaginationControlsProps) {
  const t = useTranslations('Pagination');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isFirstChange = useRef(true);

  // SearchPagination reports a 0-based page index and fires onChange(0) on mount.
  const handlePageChange = (pageIndex: number) => {
    if (isFirstChange.current) {
      isFirstChange.current = false;
      return;
    }

    const page = pageIndex + 1;
    const current = parseInt(searchParams?.get('page') || '1', 10);
    if (page === current) return;

    const params = new URLSearchParams(searchParams?.toString());
    params.set('page', page.toString());
    router.push(`${pathname}?${params.toString()}`);
  };

  // SearchPagination keeps its current page internally; remount it when the
  // search/filters or page count change so it goes back to the first page.
  const resetParams = new URLSearchParams(searchParams?.toString());
  resetParams.delete('page');

  return (
    <SearchPagination
      key={`${resetParams.toString()}|${totalPages}`}
      totalPages={totalPages}
      onChange={handlePageChange}
      boundaryCount={1}
      siblingCount={1}
      label={t("navigation")}
      nextPageAriaLabel={t("nextpage")}
      previousPageAriaLabel={t("previousPage")}
    />
  );
}
